import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getSpendByCostCenter } from 'utils/apiRequests';
import {
	TooltipHeader,
	TooltipRowPinned,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './DashboardChart.styles';
import {
	chartheight,
	FONTSTYLE,
	getSeriesData,
	getCategories,
	CHART_COLORS,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { colors } from '@alixpartners/ui-components';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';

const SpendByCostCenter = createContext();

export const SpendByCostCenterProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			type: 'bar',
			marginTop: 40,
			style: { fontFamily: 'Roboto' },
			height: chartheight,
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			title: {
				text: ``,
				style: { ...FONTSTYLE },
			},
			lineColor: colors.black,
			// categories: [],
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 4)}...`;
							}
						}
					}
				},
				style: { ...FONTSTYLE },
			},
			gridLineWidth: 0,
		},
		yAxis: {
			title: {
				align: 'high',
				text: '',
				textAlign: 'right',
				rotation: 0,
				y: -30,
				offset: 15,
				style: { ...FONTSTYLE },
			},
			gridLineWidth: 0,
			labels: {
				formatter: function() {
					return formatSpendWithDecimalAndSymbol(
						this.value,
						currencySymbol,
						hideDecimals,
						0
					).toUpperCase();
				},
				style: { ...FONTSTYLE },
			},

			lineColor: colors.black,
			lineWidth: 1,
		},
		legend: {
			enabled: false,
		},
		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				const { points, x } = this;
				const total = points && points.reduce((acc, point) => acc + point.y, 0);
				return renderToString(
					<TooltipWrapper>
						<TooltipHeader>{x}</TooltipHeader>
						<TooltipRowPinned>
							<div />
							<TooltipRowTitle>Total spend</TooltipRowTitle>
							<TooltipRowValue>
								{formatSpendWithDecimalAndSymbol(
									total,
									currencySymbol,
									hideDecimals,
									2
								).toUpperCase()}
							</TooltipRowValue>
						</TooltipRowPinned>
					</TooltipWrapper>
				);
			},
		},
		plotOptions: {
			column: {
				pointPadding: 0,
				groupPadding: 0.1,
			},
			series: {
				borderWidth: 0,
				stacking: 'normal',
				dataLabels: { enabled: false },
				states: { hover: { enabled: false } },
				events: {
					legendItemClick: function() {
						return false;
					},
				},
			},
		},
		// series: [],
		colors: [CHART_COLORS[0]],
	});

	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					getSpendByCostCenter(payload)
						.then(response => {
							if (Array.isArray(response)) {
								if (response.length > 0) {
									setData(response);
									// const [yValue, xValue] = Object.keys(response[0]);
									const series = getSeriesData(response, 'totalSpend');
									const categoriesResult = getCategories(
										response,
										'pO_COST_CENTRE'
									);
									const seriesResult = [
										{
											data: series.slice(0, 10),
										},
									];
									setOptions(prevState => ({
										...prevState,
										xAxis: {
											...prevState.xAxis,
											categories: categoriesResult,
										},
										series: seriesResult,
									}));
									setIsLoading(false);
								} else {
									setIsLoading(false);
								}
							}
						})
						.catch(() => setIsLoading(false))
						.finally(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
		]
	);

	return (
		<SpendByCostCenter.Provider value={{ isLoading, data, options }}>
			{children}
		</SpendByCostCenter.Provider>
	);
};

export const useSpendByCostCenterContext = () => {
	return useContext(SpendByCostCenter);
};
